// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — pierPlan.js   ·   THE PLAN OF THE BOARDWALK
// ────────────────────────────────────────────────────────────────────
// Everything about WHERE things are on the pier, and nothing else: the
// walkable map, every wing's shopfront, the landmarks, the furniture, the
// lamps, the solids — and the audit that proves the plan is walkable.
//
// ⚑ PURE DATA + PURE FUNCTIONS. No React, no Firebase, no three. That is
// the point of the file: `node verify/boardwalk.mjs` imports it directly
// and runs the same audit the dev boot runs, so "can you get to every door
// through a comfortable lane" is a checked fact, not an impression.
//
// ── ⭐⭐⭐ THE PITCHFORK (2026-09-24) — supersedes the six squares ──────
// caP'n: "make sure we have enough space too, Terror seems like it'd be
// tucked into a little corner … placing them side by side like that makes it
// feel too small and 'not special'." Then: "[own courts] but for all of em,
// remember this is the unstable world, it doesn't have to look like a
// standard Pier ;3 let's get creative with it … a pitch fork styled Pier."
//
//   · THE HANDLE — the entrance boardwalk, west → east: the gate, W'ily,
//     the sign; the Hall of Deeds + Crest Well in a grand court on its north
//     side, the Bazaar + Mystic Ally in a market court on its south side.
//   · THE YOKE — the crossbar, north ↔ south, joining the handle to the tines.
//   · FOUR TINES — long prongs east, tapering to points. EVERY WING STANDS
//     ALONE at the back of ITS OWN COURT (w + 5 or 6 wide, 8 deep) off its
//     tine's north side, with OPEN WATER between neighbours. No building ever
//     stands beside another.
//   · EVERY FACADE FACES THE CAMERA: every court is on its tine's NORTH side,
//     so every building faces S — tall, two storeys, lit, toward the lens
//     (L59: tall things go north). You see the next tine's backs across water.
//   · The ??? monolith stands on the point of Terror's tine; lookouts on the rest.
//
// ⚠ Change a number here and run `node verify/boardwalk.mjs` (from the app
// folder). It fails loudly on a door you cannot reach, a lane narrower than
// LANE_MIN, a building on the walkway, or anything parked in a doorway.
// ════════════════════════════════════════════════════════════════════

import { SD } from "./theme.js";

// ── THE PITCHFORK'S NUMBERS (one place; everything below derives) ──
// ⭐ 2026-09-24 (later), caP'n: "snake eyes and the other row should face towards Terror
// instead of all 4 rows facing the same way. That way too the middle of the boardwalk
// still feels like a boardwalk … we should [have] a couple connecting walkways so you
// don't have to wrap around the entire board walk just to get to the next row."
// So the two middle prongs are ONE grand BOULEVARD straight on from the handle: Terror's
// row lines its north side facing S, Snake Eyes' row its south side facing N — toward
// Terror. The outer prongs' rows face the same way as their neighbour across the water
// (north prong's row faces S, south prong's row faces N). BRIDGES cross the water gaps
// between courts, so the next row is a few steps away, never a lap of the fork.
const PRONG_W = 6;                                                   // an outer prong is 6 tiles wide
export const WALKS = [
  { id: "north", x0: 38, y0: 13, y1: 13 + PRONG_W - 1 },             // the north prong
  { id: "boulevard", x0: 38, y0: 33, y1: 44 },                       // the middle: two prongs become one grand walk
  { id: "south", x0: 38, y0: 59, y1: 59 + PRONG_W - 1 },             // the south prong
];
const COURT_D = 8;                                                   // a court's depth
const COURT_GAP = 5;                                                 // open water between neighbouring courts (a bridge crosses the middle 3)
const COURT_PAD = { 6.0: 12, 4.4: 10 };                              // court width for a frontage width (≥ 2.8 each side)
const FIRST_COURT_X = 40;                                            // the first court's west edge, past the yoke
// Each ROW of courts: which walk it opens off, which side, which wings (nearest the yoke first),
// and its stagger (alternate rows by half a court, so a building's backdrop is water, not a building).
// ⚑ A NEW TITLE IS ONE ID IN ONE OF THESE LISTS (plus its GAME_PORTALS row).
export const COURT_ROWS = [
  { walk: 0, side: "N", shift: 7, wings: ["hooked", "hoc", "vygil", "rush", "kindred", "moonshine", "sol"] },   // Hooked up front (caP'n 2026-09-25: "put Hooked way up here at the front"), Sol to the far end; Moonshine (2026-10-24) the 7th court, just before Sol
  { walk: 1, side: "N", shift: 0, wings: ["terror", "dtd", "impera", "miniwili", "jynxibash", "echoskew"] },
  { walk: 1, side: "S", shift: 7, wings: ["casino", "fractured", "perry", "88", "bodies", "duhm"] },
  { walk: 2, side: "S", shift: 0, wings: ["annihilation", "tilt", "lyraea", "swat", "bebecitos", "shatterproof"] },   // Shatterproof (2026-10-16) fills the south prong's sixth court
];
// the rows whose water gaps carry BRIDGES, and which gaps (after the k-th court)
const BRIDGED = [{ row: 1, after: [1, 3] }, { row: 2, after: [1, 3] }];
const BRIDGE_W = 3;
const WING_W = { terror: 6.0, casino: 6.0, sol: 6.0, dtd: 6.0 };    // everyone else 4.4
const wingW = (id) => WING_W[id] ?? 4.4;

export const COURTS = (() => {
  const out = [];
  COURT_ROWS.forEach((row, r) => {
    const wk = WALKS[row.walk];
    let x = FIRST_COURT_X + row.shift;
    for (const id of row.wings) {
      const w = wingW(id), cw = COURT_PAD[w];
      const [y0, y1] = row.side === "N" ? [wk.y0 - COURT_D, wk.y0 - 1] : [wk.y1 + 1, wk.y1 + COURT_D];
      out.push({ id, row: r, walk: row.walk, side: row.side, w, x0: x, x1: x + cw - 1, y0, y1 });
      x += cw + COURT_GAP;
    }
  });
  return out;
})();
const WALK_END = Math.max(...COURTS.map((c) => c.x1)) + 5;          // every prong the same length
const TIP = 6;                                                       // the point: 6 columns, a tile narrower each side every 2
WALKS.forEach((w) => { w.x1 = WALK_END; });
export const TINES = WALKS;                                          // old name
export const YOKE = { x0: 30, x1: 37, y0: WALKS[0].y0, y1: WALKS[2].y1 };
const BLVD = WALKS[1];
// the handle runs straight into the boulevard: the middle of the pier is ONE long boardwalk
export const HANDLE = { x0: 0, x1: YOKE.x0 - 1, y0: Math.round((BLVD.y0 + BLVD.y1) / 2) - 3, y1: Math.round((BLVD.y0 + BLVD.y1) / 2) + 3 };
export const HALL_COURT = { id: "hall", x0: 8, x1: 23, y0: HANDLE.y0 - 9, y1: HANDLE.y0 - 1, side: "N" };
export const MARKET_COURT = { id: "market", x0: 8, x1: 23, y0: HANDLE.y1 + 1, y1: HANDLE.y1 + 9, side: "S" };

// ⭐ THE BRIDGES — across the middle of a water gap, from the far walk to this row's walk
export const BRIDGES = BRIDGED.flatMap(({ row, after }) => {
  const rc = COURTS.filter((c) => c.row === row);
  const R = COURT_ROWS[row], wk = WALKS[R.walk];
  // the walk on the far side of this row: north rows reach up to the walk above, south rows down
  const other = R.side === "N" ? WALKS[R.walk - 1] : WALKS[R.walk + 1];
  return after.map((k) => {
    const gx0 = rc[k].x1 + 1, gx1 = rc[k + 1].x0 - 1;                 // the gap's water tiles
    const mid = Math.round((gx0 + gx1) / 2), h = (BRIDGE_W - 1) / 2;
    return R.side === "N"
      ? { x0: mid - h, x1: mid + h, y0: other.y1 + 1, y1: wk.y0 - 1 }
      : { x0: mid - h, x1: mid + h, y0: wk.y1 + 1, y1: other.y0 - 1 };
  });
});

export const COLS = WALK_END + TIP + 2;
export const ROWS = WALKS[2].y1 + COURT_D + 6;
export const T = { FLOOR: 0, WALL: 1, VOID: 2 };

// ⚑ Old names, kept so every reader still resolves: SQUARES = every court (PierInlays),
// SPINE = the handle (the gate reads it).
export const SQUARES = [HALL_COURT, MARKET_COURT, ...COURTS];
// ⚠ SPINE IS null ON THE PITCHFORK. PierInlays laid a big rose on the spine for every square
// and a brass line down both its edges — with the handle running straight into the boulevard
// that was a chain of roses down the middle of the boulevard, two overlapping (visual review,
// 2026-09-24). Every court keeps its own single rose; the gate reads HANDLE.
export const SPINE = null;
export const FLOORS = [HANDLE, YOKE, ...WALKS, HALL_COURT, MARKET_COURT, ...COURTS, ...BRIDGES];

export function buildMap() {
  const map = Array.from({ length: ROWS }, () => Array(COLS).fill(T.VOID));
  const fill = (r) => { for (let y = r.y0; y <= r.y1; y++) for (let x = r.x0; x <= r.x1; x++) map[y][x] = T.FLOOR; };
  FLOORS.forEach(fill);
  // each prong's point: a tile narrower each side every 2 columns
  for (const t of WALKS) for (let i = 1; i <= TIP; i++) {
    const k = Math.ceil(i / 2) * Math.max(1, Math.round((t.y1 - t.y0 + 1) / PRONG_W));
    for (let y = t.y0 + k; y <= t.y1 - k; y++) map[y][t.x1 + i] = T.FLOOR;
  }
  return map;
}
export const MAP = buildMap();

export function isWalkable(x, y) {
  if (x < 0 || y < 0 || x >= COLS || y >= ROWS) return false;
  return MAP[y][x] === T.FLOOR;
}

// ─────────────────────────────────────────────────────────────
// GAME PORTALS — who the wings are. WHERE they stand is SHOPFRONTS below.
// ─────────────────────────────────────────────────────────────
export const GAME_PORTALS = [
  {
    id: "terror",
    label: "Terror TCG",
    flavor: "The card game at the edge of everything.",
    icon: "🃏", color: "#7c3aed",
    verb: "Play",
    url: "https://terror-card-game.pages.dev",
    status: "live",
    districtLabel: "TERROR WING",
  },
  {
    id: "casino",
    label: "Snake Eyes Casino",
    flavor: "The house always remembers.",
    icon: "🎲", color: "#f59e0b",
    verb: "Roll",
    url: "https://snake-eyes.pages.dev",
    status: "live",
    districtLabel: "CASINO WING",
  },
  {
    id: "sol",
    label: "Sol Survivor",
    flavor: "The ruins remember what the stars forgot.",
    icon: "♥", color: "#22d3ee",
    verb: "Enter",
    url: null, status: "coming_soon",
    districtLabel: "SOL WING",
  },
  {
    id: "dtd",
    label: "Dusk till Dawn",
    flavor: "Don't let the lantern go out.",
    icon: "🕯️", color: "#f87171",
    verb: "Wander",
    url: null, status: "coming_soon",
    districtLabel: "DTD WING",
  },
  // ── 1.5 additions (2026-09-01) — registration sweep. Doors for every
  // real, substantial-codebase title in the Empire that didn't have one.
  // None of these have a confirmed deployed URL yet (checked each app's
  // CDN.js / package.json / .env — none carry a real play URL, only
  // asset-CDN placeholders), so every one below is coming_soon with
  // url:null rather than a guessed link. No `store` facade wing was
  // built for these (StorefrontFacade no-ops without one) — that is
  // set-dressing for later, not required for the door to exist or be
  // walkable-to. Positions were solved with a small placement script
  // against buildMap()'s real floor tiles so none of these — or the
  // four originals — sit on top of each other or an interactable.
  {
    id: "rush", label: "Ruin Rush",
    flavor: "The track doesn't forgive.",
    icon: "🏎", color: "#e2724f",
    verb: "Race", url: null, status: "coming_soon",
    districtLabel: "RUSH WING",
  },
  {
    id: "bodies", label: "Bodies",
    flavor: "Every bout ends the same way. Differently.",
    icon: "🥊", color: "#b33a3a",
    verb: "Fight", url: null, status: "coming_soon",
    districtLabel: "BODIES WING",
  },
  {
    id: "hoc", label: "House of Cards",
    flavor: "The house remembers every guest.",
    icon: "🏚", color: "#7a3b3b",
    verb: "Enter", url: null, status: "coming_soon",
    districtLabel: "MANOR WING",
  },
  {
    id: "hooked", label: "Hooked",
    flavor: "The Lake keeps its own hours.",
    icon: "🎣", color: "#2a7f9e",
    // ⭐ 2026-10-16 — opens on caP'n's PC through THE DEV DOOR (below), like
    // Shatterproof: "you walk into hooked, your friend can walk in right after".
    verb: "Cast", url: null, status: "coming_soon", devPort: 3020,
    districtLabel: "HOOKED WING",
  },
  {
    id: "impera", label: "Era of Imps",
    flavor: "The Luxmoire survives. You might not.",
    icon: "😈", color: "#6b4ba3",
    verb: "Descend", url: null, status: "coming_soon",
    districtLabel: "IMPERA WING",
  },
  {
    id: "jynxibash", label: "Jynxi Bash",
    flavor: "Claim your ring. Mind the seasons.",
    icon: "🚀", color: "#3d6fae",
    verb: "Roll", url: null, status: "coming_soon",
    districtLabel: "OUTER RING",
  },
  {
    id: "kindred", label: "Kindred",
    flavor: "Everyone here remembers your name.",
    icon: "💞", color: "#c97fae",
    verb: "Visit", url: null, status: "coming_soon",
    districtLabel: "KINDRED WING",
  },
  {
    id: "tilt", label: "Tilt",
    flavor: "One more credit. Always one more.",
    icon: "🕹", color: "#f0b429",
    verb: "Play", url: null, status: "coming_soon",
    districtLabel: "TILT WING",
  },
  {
    id: "88", label: "88 Ways to Die",
    flavor: "The bars fall. The page stays.",
    icon: "🎹", color: "#5a5a6e",
    verb: "Perform", url: null, status: "coming_soon",
    districtLabel: "RECITAL WING",
  },
  {
    id: "duhm", label: "Duhm",
    flavor: "Restraint compounds.",
    icon: "📖", color: "#5b8a72",
    verb: "Study", url: null, status: "coming_soon",
    districtLabel: "DUHM WING",
  },
  {
    id: "echoskew", label: "Echo-Skew",
    flavor: "Every lock has exactly one key.",
    icon: "🌀", color: "#3fb8ae",
    verb: "Breach", url: null, status: "coming_soon",
    districtLabel: "ECHO WING",
  },
  {
    id: "vygil", label: "Vy'gil",
    flavor: "Your light is dying. Flare wisely.",
    icon: "🕯", color: "#e0c94b",
    verb: "Flare", url: null, status: "coming_soon",
    districtLabel: "VYGIL WING",
  },
  {
    id: "miniwili", label: "Mini-Wili's",
    flavor: "Eighteen holes. One Ilk Ball.",
    icon: "⛳", color: "#4fa3c9",
    verb: "Putt", url: null, status: "coming_soon",
    districtLabel: "MINI-WILI'S",
  },
  {
    id: "fractured", label: "Fractured",
    flavor: "The world cracked here first.",
    icon: "🔺", color: "#6a5acd",
    verb: "Wander", url: null, status: "coming_soon",
    districtLabel: "FRACTURE WING",
  },
  // These five already had a GAME_NAMESPACES entry (economy/achievements)
  // but no physical door — colors/icons reused from that entry so a
  // being's chrome matches everywhere it appears.
  {
    id: "perry", label: "Perry",
    flavor: "The drowned mile doesn't forget a face.",
    icon: "🛡", color: "#e0763a",
    verb: "Enter", url: null, status: "coming_soon",
    districtLabel: "PERRY WING",
  },
  {
    id: "swat", label: "S'wat",
    flavor: "Crumbs pay for everything, eventually.",
    icon: "⌁", color: "#c9b48b",
    verb: "Deploy", url: null, status: "coming_soon",
    districtLabel: "S'WAT WING",
  },
  {
    id: "bebecitos", label: "Bebecitos",
    flavor: "Everything here grows if you let it.",
    icon: "🌱", color: "#7fb98a",
    verb: "Tend", url: null, status: "coming_soon",
    districtLabel: "GARDEN WING",
  },
  {
    id: "annihilation", label: "Annihilation",
    flavor: "Lyf Force or nothing at all.",
    icon: "⚔", color: "#a83b3b",
    verb: "Fold", url: null, status: "coming_soon",
    districtLabel: "ANNIHILATION WING",
  },
  {
    id: "lyraea", label: "Lyraea",
    flavor: "Every track costs exactly what it's worth.",
    icon: "♪", color: "#b98ac9",
    verb: "Listen", url: null, status: "coming_soon",
    districtLabel: "LYRAEA WING",
  },
  // ⭐ 2026-10-16 — caP'n: "let's plug in Shatterproof into the Unstable Pierre".
  // No deployed url yet, so it opens through its DEV DOOR (devPort, below).
  {
    id: "shatterproof", label: "Shatterproof",
    flavor: "Three beings. One Shatterfield. On the hour, every hour.",
    icon: "💠", color: "#7ce7ff",
    verb: "Clash", url: null, status: "coming_soon", devPort: 3036,
    districtLabel: "SHATTERPROOF WING",
  },
  // ⭐ 2026-10-24 — caP'n: "and a proper building in the UP" (for Moonshine Masters). Opens through its DEV DOOR.
  {
    id: "moonshine", label: "Moonshine Masters",
    flavor: "A little still, deep in the woods. Bring a jar.",
    icon: "🌙", color: "#e8a84a",
    verb: "Brew", url: null, status: "coming_soon", devPort: 3047,
    districtLabel: "MOONSHINE WING",
  },
];

// ⭐ THE DEV DOOR (2026-10-16). A title with no deployed url still opens on
// caP'n's PC: when the Pier itself is running on localhost, a row's `devPort`
// (its port in mass_transfer/PORTS.md) becomes its url and the door goes live.
// Deployed (pages.dev), nothing changes — the door stays "coming soon".
// ⚠ Only rows that CARRY a devPort open this way; giving every title one would
// turn twenty "coming soon" doors into links to servers that aren't running.
// ⚠ Not a social url: ieSocial's invite allowlist never sees it (invites to a
// localhost game are refused, on purpose). Party ready-up works (parties/ has no
// url check).
const ON_LOCALHOST = typeof window !== "undefined" && !!window.location &&
  /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
for (const p of GAME_PORTALS) {
  if (!p.url && p.devPort && ON_LOCALHOST) { p.url = `http://localhost:${p.devPort}`; p.status = "live"; p.dev = true; }
}

// ─────────────────────────────────────────────────────────────
// ⭐ THE SHOPFRONTS — where every wing's building stands
// ─────────────────────────────────────────────────────────────
// Each row: [id, face, edge, start, width]
//   face  — which way the DOOR faces: "S" / "N" (a north / south edge of the
//           deck) or "E" / "W" (the west / east edge of a square).
//   edge  — the deck edge line it stands on: a y for S/N, an x for E/W.
//           The building sits OFF the walkable floor, over the sea.
//   start, width — its frontage along that edge (x for S/N, y for E/W).
// A portal's x/y/rx/ry are DERIVED from its row, so a door and its doorstep
// can never drift apart.
// ⚑ A NEW TITLE IS ONE ROW HERE (plus its GAME_PORTALS entry). If there is
// no frontage left, add a square to SQUARES and give its edges rows.
export const SHOPFRONTS = COURTS.map((c) => {
  // the building stands centred on its court's far edge, facing its walk
  const start = +(c.x0 - 0.5 + ((c.x1 - c.x0 + 1) - c.w) / 2).toFixed(2);
  // north-side courts: the building on the court's north edge, door facing S; south-side: the reverse
  return c.side === "N" ? [c.id, "S", c.y0 - 0.5, start, c.w] : [c.id, "N", c.y1 + 0.5, start, c.w];
});

// The Hall of Deeds: the far edge of the civic square, facing the camera
// (tall things go north), with the Crest Well in the square before it.
export const HALL_FRONT = { y: HALL_COURT.y0 - 0.5, x: (HALL_COURT.x0 + HALL_COURT.x1) / 2 - 3.2, w: 6.4, face: "S" };

const isNS = (face) => face === "S" || face === "N";

// Tall (two storeys) when it stands north of the boardwalk. L59.
function tallFor(face, edge, start, w) {
  // ⚑ Every wing is two storeys on the pitchfork — caP'n turned the south rows to face
  // Terror ("they all deserve an immaculate building"). L59's low south row is retired
  // for the COURTS; the camera fader still cuts away anything between lens and player.
  if (face === "S" || face === "N") return true;
  return start + w / 2 < HANDLE.y0;          // (no side-facing fronts on the pitchfork; kept for the exception)
}

// The doorstep: centred on the frontage, 0.9 tiles in off the edge.
export function doorOf(face, edge, start, w) {
  const mid = start + w / 2;
  if (face === "S") return { x: mid, y: edge + 0.9 };
  if (face === "N") return { x: mid, y: edge - 0.9 };
  if (face === "E") return { x: edge + 0.9, y: mid };
  return { x: edge - 0.9, y: mid };
}

(() => {
  const byId = Object.fromEntries(SHOPFRONTS.map((r) => [r[0], r]));
  for (const p of GAME_PORTALS) {
    const r = byId[p.id];
    if (!r) {
      // A title with no frontage has no door. Loud, never a crash; the
      // audit counts it as a failure.
      p.x = -99; p.y = -99; p.rx = 0; p.ry = 0;
      continue;
    }
    const [, face, edge, start, w] = r;
    // `store.x/y` keep their old meaning for a S/N front (x0, edge line) so
    // the 2D fossil renderer still reads them; for E/W they are (edge, y0).
    p.store = isNS(face)
      ? { face, edge, start, w, x: start, y: edge, windows: w >= 5 ? 3 : 2, tall: tallFor(face, edge, start, w) }
      : { face, edge, start, w, x: edge, y: start, windows: 2, tall: tallFor(face, edge, start, w) };
    const d = doorOf(face, edge, start, w);
    p.x = d.x; p.y = d.y;
    // ⚠ TIGHT, because the proximity test takes the FIRST match, not the
    // nearest — neighbours' doorsteps must never overlap.
    const along = Math.max(1, w / 2 - 0.15);
    p.rx = isNS(face) ? along : 1.0;
    p.ry = isNS(face) ? 1.0 : along;
  }
})();

// Every frontage, normalised for the renderers: `a0..a1` runs along the edge.
export const FRONTS = [
  ...GAME_PORTALS.filter((p) => p.store).map((p) => ({
    id: p.id, face: p.store.face, edge: p.store.edge, a0: p.store.start, a1: p.store.start + p.store.w,
  })),
  { id: "hall", face: HALL_FRONT.face, edge: HALL_FRONT.y, a0: HALL_FRONT.x, a1: HALL_FRONT.x + HALL_FRONT.w },
];

// ─────────────────────────────────────────────────────────────
// ⭐ THE RECRUITS — one being out front of every building (2026-09-24)
// ─────────────────────────────────────────────────────────────
// caP'n: "create a proper person infront of every building. use 1 of the many
// available models from Annihilation (beings only) not just the Terror card beings,
// you can use the other cannon beings as well. Just have them stand in the front and
// you can interact with them to start their challenges etc (if they have no function
// leave as is, we're just putting placeholders nothing fancy)."
// Each row: wing → [kit, being id, tier, display name, the pitch]. `kit` is "lineage"
// (ieBeingModels · IEBeing) or "species" (ieTerrorSpecies · TerrorSpecies) — the SAME
// bodies Annihilation draws (byte-identical kits; the LAW OF ONE BODY). Swapping a
// recruit is one row. ⚑ Placeholders: the pitch is a line, the choice is caP'n's to rewrite.
export const RECRUITS = {
  terror:       ["lineage", "xulan", "lord", "Os'orun-Veth", "The cards remember every hand you've ever lost. Sit. Deal. Let's see what they remember of you."],
  casino:       ["lineage", "inverted_serpent", "champion", "Inverted Serpent", "Two dice, one serpent, and the house never forgets a face. Roll with me."],
  annihilation: ["lineage", "spectral_tiger", "lord", "S'aolesh", "Lyf Force or nothing at all. Walk the Fold with me, if you can keep up."],
  sol:          ["species", "aeolix", "champion", "Twinkling Aeolix", "The ruins remember what the stars forgot. Bring fire. You'll need it."],
  dtd:          ["species", "slijj", "beb", "Slijj", "Don't let the lantern go out. I've watched too many of them go out."],
  rush:         ["lineage", "z4z", "champion", "Z4Z", "The track doesn't forgive. Neither do I. Engines up."],
  bodies:       ["lineage", "h09", "champion", "H-09 Neutron Star", "Diplomacy is for the other H. Put your hands up."],
  hoc:          ["species", "carnifex", "beb", "Lustered Carnifex", "The house remembers every guest. It's been asking after you."],
  hooked:       ["species", "calypsis", "champion", "Calypsis", "The Lake keeps its own hours. Sun above, moon below. Cast when I say."],
  impera:       ["species", "malumora", "champion", "Radiant Malumora", "The Luxmoire survives. You might not. That's what makes it fun."],
  jynxibash:    ["lineage", "lucky_dragon", "champion", "Lucky Dragon", "Claim your ring. Mind the seasons. J'aeonxi is watching the dice with you."],
  kindred:      ["species", "bunnovian", "beb", "Spectral Bunnovian", "Everyone here remembers your name. Come in — they've been waiting."],
  tilt:         ["lineage", "antdroid", "beb", "Antdroid", "One more credit. Always one more. Feed the machine."],
  "88":         ["species", "lyrixi", "champion", "Lyrixi", "The bars fall. The page stays. Eighty-eight keys, and every one of them can kill you."],
  duhm:         ["species", "mygzori", "beb", "Mygzori", "Restraint compounds. Sit, and study with me."],
  echoskew:     ["species", "riven", "beb", "Riven", "Every lock has exactly one key. I am two pieces. Guess which one you need."],
  vygil:        ["species", "aeternavis", "champion", "Radiant Aeternavis", "Your light is dying. Mine never has. Flare wisely."],
  miniwili:     ["species", "xolibrix", "beb", "Spectral Xolibrix", "Eighteen holes. One Ilk Ball. I never stop moving — try to keep up."],
  fractured:    ["species", "hyalayri", "champion", "Radiant Hyalayri", "The world cracked here first. You can see straight through me. It won't help."],
  perry:        ["species", "tzhahws", "champion", "Tzhahws Buccaneer", "The drowned mile doesn't forget a face. Parry, or pay the toll."],
  swat:         ["species", "skuryz", "beb", "Skuryz", "Crumbs pay for everything, eventually. We'll take the odds you were counting on."],
  bebecitos:    ["species", "embearaea", "beb", "Embearaea", "Everything here grows if you let it! Come see, come see!"],
  lyraea:       ["species", "lyraei", "beb", "Radiant Lyraei", "Every track costs exactly what it's worth. Listen with me a while."],
  moonshine:    ["lineage", "shadow_fox", "champion", "Shadow Fox", "Psst. There's a still out past the pines. I chase folks who go looking... but I'll look the other way. Mostly."],
  shatterproof: ["lineage", "chaos_dragon", "lord", "Xerath'oun", "Three on three, and you aim it all yourself. There's a tournament every hour. You in, or just betting?"],
};
// where a recruit stands: out front of its building, a step to the door's side and
// clear of the door's apron (so the door and the recruit never share a prompt)
export const RECRUIT_POS = (() => {
  const out = {};
  for (const [id, face, edge, start, w] of SHOPFRONTS) {
    const mid = start + w / 2, dir = face === "S" ? 1 : -1;
    out[id] = { x: mid + 1.55, y: edge + dir * 3.2, face };
  }
  return out;
})();

// ─────────────────────────────────────────────────────────────
// INTERACTABLES — the landmarks and the keeper
// ─────────────────────────────────────────────────────────────
// ⚠ ORDER IS LOAD-BEARING: the proximity test takes the FIRST match.
// `solid` is a collision radius in tiles, or a list of [dx, dy, r] circles
// for anything that is not round.
export const INTERACTABLES = [
  { id: "wily",        label: "W'ily",            verb: "Speak",  color: SD.purpleLight, x: 4.5,  y: HANDLE.y1 - 1, rx: 1.6, ry: 1.4, solid: [[0, 0, 0.4], [-0.3, -0.85, 0.42]] },   // the post + Willie behind it
  { id: "home",        label: "Your Hall",        verb: "Enter",  color: "#f0d060",      x: (HALL_COURT.x0 + HALL_COURT.x1) / 2, y: HALL_COURT.y0 + 0.4, rx: 2.4, ry: 1.0 },
  { id: "crest_well",  label: "Crest Well",       verb: "Offer",  color: SD.gold,        x: (HALL_COURT.x0 + HALL_COURT.x1) / 2, y: HALL_COURT.y1 - 2.2, rx: 1.8, ry: 1.8, solid: 1.05 },
  { id: "old_sign",    label: "Weathered Sign",   verb: "Read",   color: "#8b7355",      x: 0.4,  y: HANDLE.y0 + 0.6, rx: 1.0, ry: 1.0, solid: 0.3 },
  { id: "bazaar",      label: "Boardwalk Bazaar", verb: "Browse", color: "#f472b6",      x: MARKET_COURT.x0 + 4, y: MARKET_COURT.y0 + 4.2, rx: 2.4, ry: 2.0, solid: [[0, 0, 1.0], [-1.2, 0, 0.75], [1.2, 0, 0.75], [-1.9, 0.7, 0.4], [1.95, 0.55, 0.35]] },
  { id: "marketplace", label: "Bonnie",           verb: "Browse", color: "#a78bfa",      x: MARKET_COURT.x1 - 3.5, y: MARKET_COURT.y0 + 4.2, rx: 2.4, ry: 1.9, solid: [[0, 0, 1.0], [-1.1, 0, 0.75], [1.1, 0, 0.75], [-2.35, 0.2, 0.55]] },
  // the thing nobody can explain, on the point of the boulevard — Terror's walk
  { id: "mystery",     label: "???",              verb: "???",    color: "#475569",      x: WALK_END + 2.5, y: (BLVD.y0 + BLVD.y1) / 2, rx: 1.6, ry: 1.8, locked: true, solid: 0.45 },
  // the recruits, last: nothing above may be shadowed by one (first-match)
  ...Object.entries(RECRUITS).filter(([id]) => RECRUIT_POS[id]).map(([id, [kit, being, tier, name]]) => ({
    id: `recruit_${id}`, recruit: { wing: id, kit, being, tier }, label: name, verb: "Talk",
    color: "#e8d9ff", x: RECRUIT_POS[id].x, y: RECRUIT_POS[id].y, rx: 1.1, ry: 1.1, solid: 0.35,
    face: RECRUIT_POS[id].face,
  })),
];

// ⭐ THE BOARDWALK FURNITURE — against rails and in square corners, never
// in a lane and never before a door. Solid; the audit proves it.
const PLANTER = [[0, -0.35, 0.35], [0, 0.35, 0.35]];
const BENCH_X = [[-0.45, 0, 0.4], [0.45, 0, 0.4]];
export const PIER_PROPS = [
  // the handle: benches on its rails looking out to sea, between the courts and the yoke
  { kind: "bench", x: 26.5, y: HANDLE.y0 - 0.2, rot: 0,       solid: BENCH_X },
  { kind: "bench", x: 26.5, y: HANDLE.y1 + 0.2, rot: Math.PI, solid: BENCH_X },
  // the yoke: an anchor at its north end, a planter tree at its south end
  { kind: "anchor", x: (YOKE.x0 + YOKE.x1) / 2, y: YOKE.y0 + 1.5, rot: 0, solid: [[0, 0, 0.8]] },
  { kind: "tree",   x: (YOKE.x0 + YOKE.x1) / 2, y: YOKE.y1 - 1.5, rot: 0, solid: [[0, 0, 1.0]] },
  // every court's mouth: a planter each side, against the court's side rails
  ...COURTS.flatMap((c) => {
    const my = c.side === "N" ? c.y1 - 0.4 : c.y0 + 0.4;          // the mouth is the edge on the walk
    return [
      { kind: "planter", x: c.x0 + 0.1, y: my, rot: Math.PI / 2,  solid: PLANTER },
      { kind: "planter", x: c.x1 - 0.1, y: my, rot: -Math.PI / 2, solid: PLANTER },
    ];
  }),
  // ⭐ every prong ends in a lookout: a bench on each rail, a coin telescope at the point
  ...WALKS.flatMap((w, t) => [
    { kind: "bench", x: WALK_END - 1.5, y: w.y0 - 0.2, rot: 0,       solid: BENCH_X },
    { kind: "bench", x: WALK_END - 1.5, y: w.y1 + 0.2, rot: Math.PI, solid: BENCH_X },
    ...(t === 1 ? [] : [{ kind: "scope", x: WALK_END + 3.0, y: (w.y0 + w.y1) / 2, rot: 0, solid: [[0, 0, 0.3]] }]),
  ]),
];

export const SPAWN = { x: 7, y: (HANDLE.y0 + HANDLE.y1) / 2 };

// The entrance gate spans the boardwalk's west end.
export const GATE = { x: -0.62, z0: HANDLE.y0 - 0.65, z1: HANDLE.y1 + 0.65 };

// ─────────────────────────────────────────────────────────────
// LAMPPOSTS — in the alleys and at the squares' mouths, never before a door
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// ALLEYS — the gaps between neighbouring buildings on one edge
// ─────────────────────────────────────────────────────────────
// The deck closes each with a board fence and one lamp. Derived from the
// fronts, so a moved shopfront moves its alley.
export const ALLEYS = (() => {
  const out = [];
  for (const f of FRONTS) {
    const next = FRONTS
      .filter((g) => g !== f && g.face === f.face && Math.abs(g.edge - f.edge) < 0.01 && g.a0 >= f.a1 - 0.001)
      .sort((a, b) => a.a0 - b.a0)[0];
    if (next && next.a0 - f.a1 <= 4) out.push({ face: f.face, edge: f.edge, s0: f.a1, s1: next.a0 });
  }
  return out;
})();

export const LAMPPOSTS = (() => {
  const out = [];
  // ⚠ ONE LAMP TO A PLACE. Two lamps 1.2 apart in every alley and a third at
  // every square's corner read as a thicket of poles (visual review,
  // 2026-09-14). A lamp is skipped when one already stands within `near`.
  const push = (x, y, near) => {
    if (out.some((l) => Math.hypot(l.x - x, l.y - y) < near)) return;
    out.push({ x, y });
  };
  // ⚠ ON THE RAIL LINE, not 0.18 inside it: there a lamp took a bite out of
  // the boardwalk's outer rows and a walk along the rail snagged on every
  // one. At 0.43 out the walker's centre still passes along row 12 and 18.
  const RAIL = 0.43;
  // ⭐ THE PITCHFORK'S LAMPS: at the two corners of every court's mouth (on its
  // tine's rail), along both rails of the handle and the yoke, and down each
  // tine's south rail — every ~7 tiles, never inside a mouth or a junction.
  // a lamp each side of both mouths of every bridge, ON the walk's rail line (0.07 in):
  // a lit crossing you can see from down the walk
  for (const b of BRIDGES) for (const y of [b.y0 - 0.57, b.y1 + 0.57]) {
    push(b.x0 - 0.9, y, 1.2); push(b.x1 + 0.9, y, 1.2);
  }
  for (const c of COURTS) {
    const wk = WALKS[c.walk];
    const y = c.side === "N" ? wk.y0 - RAIL : wk.y1 + RAIL;
    push(c.x0 - 1.1, y, 4); push(c.x1 + 1.1, y, 4);             // one lamp to a water gap, not two
  }
  const mouthAt = (x, y) => SQUARES.some((q) => x > q.x0 - 1.2 && x < q.x1 + 1.2 && Math.abs(y - (q.side === "N" ? q.y1 + 0.5 : q.y0 - 0.5)) < 1);
  const railRun = (x0, x1, y, step = 7) => { for (let x = x0; x <= x1; x += step) if (!mouthAt(x, y)) push(x, y, 2.5); };
  railRun(3, HANDLE.x1 - 3, HANDLE.y0 - RAIL);
  railRun(3, HANDLE.x1 - 3, HANDLE.y1 + RAIL);
  // a walk's rails only where the rail really faces the sea (not a court's or a bridge's mouth)
  const seaAt = (x, y) => !isWalkable(Math.round(x), y);
  for (const w of WALKS) {
    for (let x = w.x0 + 3; x <= w.x1 - 2; x += 7) {
      if (seaAt(x, w.y0 - 1) && seaAt(x - 1, w.y0 - 1) && seaAt(x + 1, w.y0 - 1)) push(x, w.y0 - RAIL, 2.5);
      if (seaAt(x, w.y1 + 1) && seaAt(x - 1, w.y1 + 1) && seaAt(x + 1, w.y1 + 1)) push(x, w.y1 + RAIL, 2.5);
    }
  }
  for (let y = YOKE.y0 + 3; y <= YOKE.y1 - 3; y += 7) {
    const junction = [HANDLE, ...WALKS].some((w) => y > w.y0 - 1.5 && y < w.y1 + 1.5);
    // ⚠ 4, not 2.5: at 2.5 a yoke lamp stood 3 tiles from the court-corner lamp at Terror's mouth — a pair of poles
    if (!junction) { push(YOKE.x0 - RAIL, y, 4); push(YOKE.x1 + RAIL, y, 4); }
  }
  // ⚠ Beside a building the lamp stands ON the edge line too (0.07 in, not
  // 0.32): at 0.32 the lamp by DUHM took row 12 off the boardwalk’s first
  // stretch. Its reach (0.12 + a body) now ends short of the first row’s centre.
  const IN = 0.07;
  // one lamp in the middle of every alley, in front of its fence
  for (const g of ALLEYS) {
    const inward = (g.face === "S" || g.face === "E") ? IN : -IN, mid = (g.s0 + g.s1) / 2;
    if (isNS(g.face)) push(mid, g.edge + inward, 1.5); else push(g.edge + inward, mid, 1.5);
  }
  // and the open end of a frontage with no neighbour, unless a lamp is already near
  for (const f of FRONTS) {
    const inward = (f.face === "S" || f.face === "E") ? IN : -IN;
    const sameLine = (g) => g.face === f.face && Math.abs(g.edge - f.edge) < 0.01;
    const ends = [];
    if (!ALLEYS.some((g) => sameLine(g) && Math.abs(g.s1 - f.a0) < 0.01)) ends.push(f.a0 - 0.6);
    if (!ALLEYS.some((g) => sameLine(g) && Math.abs(g.s0 - f.a1) < 0.01)) ends.push(f.a1 + 0.6);
    for (const a of ends) { if (isNS(f.face)) push(a, f.edge + inward, 2.5); else push(f.edge + inward, a, 2.5); }
  }
  return out;
})();

// ─────────────────────────────────────────────────────────────
// ⭐ THE SOLIDS — things you walk ROUND
// ─────────────────────────────────────────────────────────────
// ⭐ caP'n 2026-09-25: these pieces are turned 180° about their own spot (Pier3D.jsx),
// so their collision circles are mirrored here to match.
export const TURNED_AROUND = new Set(["wily", "bazaar", "marketplace"]);
for (const n of INTERACTABLES) {
  if (TURNED_AROUND.has(n.id) && Array.isArray(n.solid)) n.solid = n.solid.map(([dx, dy, r]) => [-dx, -dy, r]);
}
const circlesOf = (o) => (Array.isArray(o.solid)
  ? o.solid.map(([dx, dy, r]) => ({ x: o.x + dx, y: o.y + dy, r }))
  : [{ x: o.x, y: o.y, r: o.solid }]);

export const SOLIDS = [
  ...INTERACTABLES.filter((n) => n.solid).flatMap(circlesOf),
  ...LAMPPOSTS.map((l) => ({ x: l.x, y: l.y, r: 0.12 })),
  ...PIER_PROPS.flatMap(circlesOf),
];
export const BODY_R = 0.28;   // the walker's own radius
export function blockedBySolid(x, y) {
  for (const o of SOLIDS) {
    const dx = x - o.x, dy = y - o.y, rr = o.r + BODY_R;
    if (dx * dx + dy * dy < rr * rr) return true;
  }
  return false;
}
// Where the walker's CENTRE may stand — the same test the movement loop runs.
export const standable = (x, y) => isWalkable(Math.round(x), Math.round(y)) && !blockedBySolid(x, y);

// The proximity rule the live screen uses (ProximitySystem.getNearby):
// the FIRST object whose rect contains the point.
export function firstMatch(pos, objects) {
  return objects.find((o) => Math.abs(pos.x - o.x) <= (o.rx ?? 2) && Math.abs(pos.y - o.y) <= (o.ry ?? 1)) || null;
}

// ════════════════════════════════════════════════════════════════════
// ⭐⭐ THE AUDIT — is this a place you can actually walk around?
// ════════════════════════════════════════════════════════════════════
export const LANE_MIN = 1.6;        // the narrowest a route to anything may get, in tiles of free walking width
const BUILD_DEPTH = 3.15;           // a shopfront's footprint back from its edge (apron included)
const MIN_GAP = 1.6;                // the least space between two buildings on one edge
const DOOR_APRON = { width: 1.8, depth: 2.5 };   // the patch before a door nothing may stand in

function footprintOf(f) {
  const back = (f.face === "S" || f.face === "E") ? f.edge - BUILD_DEPTH : f.edge + BUILD_DEPTH;
  const lo = Math.min(f.edge, back), hi = Math.max(f.edge, back);
  return isNS(f.face)
    ? { x0: f.a0 - 0.08, x1: f.a1 + 0.08, y0: lo, y1: hi }
    : { x0: lo, x1: hi, y0: f.a0 - 0.08, y1: f.a1 + 0.08 };
}
function apronOf(f) {
  const mid = (f.a0 + f.a1) / 2, hw = DOOR_APRON.width / 2, d = DOOR_APRON.depth;
  if (f.face === "S") return { x0: mid - hw, x1: mid + hw, y0: f.edge, y1: f.edge + d };
  if (f.face === "N") return { x0: mid - hw, x1: mid + hw, y0: f.edge - d, y1: f.edge };
  if (f.face === "E") return { x0: f.edge, x1: f.edge + d, y0: mid - hw, y1: mid + hw };
  return { x0: f.edge - d, x1: f.edge, y0: mid - hw, y1: mid + hw };
}
const rectsOverlap = (a, b, eps = 0.02) =>
  a.x0 < b.x1 - eps && b.x0 < a.x1 - eps && a.y0 < b.y1 - eps && b.y0 < a.y1 - eps;
const circleInRect = (c, r) => {
  const nx = Math.max(r.x0, Math.min(c.x, r.x1)), ny = Math.max(r.y0, Math.min(c.y, r.y1));
  return (c.x - nx) ** 2 + (c.y - ny) ** 2 < c.r * c.r;
};

export function auditPlan({ step = 0.2 } = {}) {
  const problems = [];

  // ── 1 · every wing has a door, and every door and landmark is selectable ──
  for (const p of GAME_PORTALS) {
    if (!p.store) { problems.push(`${p.id}: no row in SHOPFRONTS — this wing has no door`); continue; }
    if (!standable(p.x, p.y)) problems.push(`${p.id}: its doorstep (${p.x.toFixed(1)}, ${p.y.toFixed(1)}) is not standable ground`);
  }
  const selects = (o, x, y) => {
    if (!standable(x, y)) return false;
    const isPortal = GAME_PORTALS.includes(o);
    if (isPortal) return firstMatch({ x, y }, GAME_PORTALS) === o;
    if (firstMatch({ x, y }, GAME_PORTALS)) return false;          // portals win
    return firstMatch({ x, y }, INTERACTABLES) === o;
  };
  const TARGETS = [...GAME_PORTALS.filter((p) => p.store), ...INTERACTABLES];

  // ── 2 · buildings: no overlaps, none on the walkway, room between neighbours ──
  const prints = FRONTS.map((f) => ({ f, r: footprintOf(f) }));
  for (let i = 0; i < prints.length; i++) {
    for (let j = i + 1; j < prints.length; j++) {
      if (rectsOverlap(prints[i].r, prints[j].r)) problems.push(`${prints[i].f.id} and ${prints[j].f.id}: buildings overlap`);
    }
    const r = prints[i].r;
    for (let y = Math.max(0, Math.floor(r.y0)); y <= Math.min(ROWS - 1, Math.ceil(r.y1)); y++) {
      for (let x = Math.max(0, Math.floor(r.x0)); x <= Math.min(COLS - 1, Math.ceil(r.x1)); x++) {
        if (MAP[y][x] === T.FLOOR && rectsOverlap(r, { x0: x - 0.5, x1: x + 0.5, y0: y - 0.5, y1: y + 0.5 }, 0.05)) {
          problems.push(`${prints[i].f.id}: its building stands on the walkway at tile (${x}, ${y})`);
          y = Infinity; break;
        }
      }
    }
  }
  const byEdge = {};
  for (const f of FRONTS) (byEdge[`${f.face}|${f.edge}`] ||= []).push(f);
  for (const list of Object.values(byEdge)) {
    list.sort((a, b) => a.a0 - b.a0);
    for (let i = 0; i + 1 < list.length; i++) {
      const gap = list[i + 1].a0 - list[i].a1;
      if (gap < MIN_GAP) problems.push(`${list[i].id} and ${list[i + 1].id}: only ${gap.toFixed(2)} tiles apart (min ${MIN_GAP})`);
    }
  }

  // ── 3 · nothing stands in a doorway ──
  const solidOwners = [
    ...INTERACTABLES.filter((n) => n.solid).flatMap((n) => circlesOf(n).map((c) => ({ ...c, who: n.id }))),
    ...LAMPPOSTS.map((l) => ({ x: l.x, y: l.y, r: 0.12, who: "a lamppost" })),
    ...PIER_PROPS.flatMap((p) => circlesOf(p).map((c) => ({ ...c, who: `a ${p.kind}` }))),
  ];
  for (const f of FRONTS) {
    const apron = apronOf(f);
    const hit = solidOwners.find((c) => circleInRect(c, apron));
    if (hit) problems.push(`${f.id}: ${hit.who} stands in front of its door`);
  }

  // ── 4 · ⭐ THE LANES — every target through a comfortable width ──
  // A clearance map: for every standable sample, the distance to the nearest
  // place the walker's centre cannot go. A route is only as wide as its
  // narrowest point; the widest-route width to each target is reported.
  const W = Math.round((COLS + 1) / step), H = Math.round((ROWS + 1) / step);
  const X = (i) => -0.5 + (i + 0.5) * step, Y = (j) => -0.5 + (j + 0.5) * step;
  const N = W * H;
  const free = new Uint8Array(N);
  for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) free[j * W + i] = standable(X(i), Y(j)) ? 1 : 0;
  const dist = new Float32Array(N);
  for (let k = 0; k < N; k++) dist[k] = free[k] ? 1e6 : 0;
  const R2 = Math.SQRT2;
  for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) {
    const k = j * W + i; if (!free[k]) continue;
    let d = dist[k];
    if (i > 0) d = Math.min(d, dist[k - 1] + 1);
    if (j > 0) d = Math.min(d, dist[k - W] + 1);
    if (i > 0 && j > 0) d = Math.min(d, dist[k - W - 1] + R2);
    if (i < W - 1 && j > 0) d = Math.min(d, dist[k - W + 1] + R2);
    dist[k] = d;
  }
  for (let j = H - 1; j >= 0; j--) for (let i = W - 1; i >= 0; i--) {
    const k = j * W + i; if (!free[k]) continue;
    let d = dist[k];
    if (i < W - 1) d = Math.min(d, dist[k + 1] + 1);
    if (j < H - 1) d = Math.min(d, dist[k + W] + 1);
    if (i < W - 1 && j < H - 1) d = Math.min(d, dist[k + W + 1] + R2);
    if (i > 0 && j < H - 1) d = Math.min(d, dist[k + W - 1] + R2);
    dist[k] = d;
  }
  // clearance in tiles; a lane of width w needs clearance ≥ w/2
  const clear = (k) => dist[k] * step;
  const idx = (x, y) => {
    const i = Math.round((x + 0.5) / step - 0.5), j = Math.round((y + 0.5) / step - 0.5);
    return (i < 0 || j < 0 || i >= W || j >= H) ? -1 : j * W + i;
  };

  // selecting samples of each target, and the free samples next to them
  const targetSamples = TARGETS.map((o) => {
    const ks = [];
    for (let y = o.y - (o.ry ?? 1); y <= o.y + (o.ry ?? 1) + 1e-6; y += step) {
      for (let x = o.x - (o.rx ?? 2); x <= o.x + (o.rx ?? 2) + 1e-6; x += step) {
        if (selects(o, x, y)) { const k = idx(x, y); if (k >= 0 && free[k]) ks.push(k); }
      }
    }
    return { o, ks };
  });
  for (const t of targetSamples) if (!t.ks.length) problems.push(`${t.o.id}: no standable spot selects it`);

  const spawnK = idx(SPAWN.x, SPAWN.y);
  if (spawnK < 0 || !free[spawnK]) problems.push("SPAWN is not standable ground");

  // flood through samples with clearance ≥ half; allow a short final
  // approach (≤ APPROACH tiles) through narrower ground, like stepping up to
  // a counter
  const APPROACH = 1.0;
  const reachAt = (half) => {
    const seen = new Uint8Array(N);
    if (spawnK < 0 || !free[spawnK]) return seen;
    const q = new Int32Array(N); let qh = 0, qt = 0;
    // start from the nearest lane sample to spawn
    if (clear(spawnK) + 1e-6 >= half) { seen[spawnK] = 1; q[qt++] = spawnK; }
    while (qh < qt) {
      const k = q[qh++], i = k % W, j = (k / W) | 0;
      for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const ni = i + di, nj = j + dj;
        if (ni < 0 || nj < 0 || ni >= W || nj >= H) continue;
        const nk = nj * W + ni;
        if (seen[nk] || !free[nk] || clear(nk) + 1e-6 < half) continue;
        seen[nk] = 1; q[qt++] = nk;
      }
    }
    // the short approach
    const hops = Math.ceil(APPROACH / step);
    let frontier = [];
    for (let k = 0; k < N; k++) if (seen[k] === 1) frontier.push(k);
    for (let h = 0; h < hops; h++) {
      const next = [];
      for (const k of frontier) {
        const i = k % W, j = (k / W) | 0;
        for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const ni = i + di, nj = j + dj;
          if (ni < 0 || nj < 0 || ni >= W || nj >= H) continue;
          const nk = nj * W + ni;
          if (seen[nk] || !free[nk]) continue;
          seen[nk] = 2; next.push(nk);
        }
      }
      frontier = next;
    }
    return seen;
  };
  const widths = [3.0, 2.6, 2.2, 2.0, 1.8, 1.6, 1.4, 1.2, 1.0, 0.8, 0.6, 0.4];
  const best = new Map();
  for (const w of widths) {
    const seen = reachAt(w / 2);
    for (const t of targetSamples) {
      if (best.has(t.o.id)) continue;
      if (t.ks.some((k) => seen[k])) best.set(t.o.id, w);
    }
  }
  const routeWidth = {};
  for (const t of targetSamples) {
    const w = best.get(t.o.id) ?? 0;
    routeWidth[t.o.id] = w;
    if (w < LANE_MIN) problems.push(`${t.o.id}: the best route to it narrows to ${w ? `${w.toFixed(1)} tiles` : "nothing — unreachable"} (min ${LANE_MIN})`);
  }

  // how much of the walkable deck is lane-width at all, for the record
  let laneSamples = 0, freeSamples = 0;
  for (let k = 0; k < N; k++) if (free[k]) { freeSamples++; if (clear(k) >= LANE_MIN / 2) laneSamples++; }

  return {
    problems,
    stats: {
      narrowestRoute: Math.min(...Object.values(routeWidth)),
      routeWidth,
      laneShare: freeSamples ? laneSamples / freeSamples : 0,
      buildings: FRONTS.length, lamps: LAMPPOSTS.length, props: PIER_PROPS.length,
    },
  };
}

// The dev-boot face of the audit: named problems in the console.
export function auditBoardwalk() {
  const { problems, stats } = auditPlan();
  /* eslint-disable no-console */
  if (problems.length) console.warn(`[PIER] boardwalk plan — ${problems.length} problem(s):\n  ` + problems.join("\n  "));
  else console.info(`[PIER] boardwalk plan is sound: every door and landmark reachable through lanes ≥ ${stats.narrowestRoute.toFixed(1)} tiles wide.`);
  /* eslint-enable no-console */
}
